import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  StyledInput,
  StyledInputContainer,
  StyledInputGroup,
  StyledLabel,
  StyledSelect,
  StyledSelectContainer,
  StyledSelectGroup,
  StyledTextarea,
} from "../../../StyledCss/styledInputs";
import { PrimaryButton, SecondaryButton } from "../../../StyledCss/styledbtn";
import { ErrorText } from "../../../StyledCss/styledComponents";
import { validateTextField, validateNumericField } from "../../../util/hiringUtils";
import "./index.css";

const EMPLOYMENT_TYPES = ["Full-time", "Part-time", "Contract", "Internship", "Freelance"];

const getMinDate = () => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().split("T")[0];
};

const formatDateToInput = (displayDate) => {
  if (!displayDate) return "";
  const parts = displayDate.split("-");
  if (parts.length !== 3) return "";
  return `${parts[2]}-${parts[1]}-${parts[0]}`;
};

const CreateJobModal = ({ onSaveDraft, onPublish, onUpdate, onCancel, editJob }) => {
  const orgDetails = JSON.parse(localStorage.getItem("org"));
  const primaryColor = orgDetails?.primaryColor || "#e86020";

  const [skills, setSkills] = useState([]);
  const [skillInput, setSkillInput] = useState("");
  const [skillsError, setSkillsError] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    trigger,
    getValues,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    setSkillsError(false);
    if (editJob) {
      setSkills(
        Array.isArray(editJob.skills)
          ? editJob.skills
          : editJob.skills
          ? editJob.skills.split(",").map((s) => s.trim()).filter(Boolean)
          : []
      );
      reset({
        title:          editJob.title          || "",
        designation:    editJob.designation    || "",
        employmentType: editJob.employmentType || "",
        location:       editJob.location       || "",
        vacancies:      editJob.vacancies      || "",
        experience:     editJob.experience     || "",
        description:    editJob.description    || "",
        publishedDate:  formatDateToInput(editJob.publishedDate),
      });
    } else {
      setSkills([]);
      setSkillInput("");
      reset({
        title: "", designation: "", employmentType: "",
        location: "", vacancies: "", experience: "", description: "",
        publishedDate: "",
      });
    }
  }, [editJob, reset]);

  const addSkill = () => {
    const trimmed = skillInput.trim();
    if (trimmed && !skills.includes(trimmed)) {
      setSkills((prev) => [...prev, trimmed]);
      setSkillsError(false);
    }
    setSkillInput("");
  };

  const handleSkillKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addSkill();
    }
  };

  const removeSkill = (skill) => {
    setSkills((prev) => prev.filter((s) => s !== skill));
  };

  // Backend requires every one of these keys to be present on the request
  // body (they're non-Optional in the API schema) even when the job is being
  // saved as an incomplete DRAFT — it only rejects a *missing* job_title, not
  // blank/zero values for the rest. So every field here falls back to a safe
  // empty value instead of throwing (data.title.trim()) or serializing to
  // `null` (parseInt("") -> NaN -> JSON "null"), which is what was silently
  // failing "Save as Draft" whenever the form wasn't fully filled in.
  const buildJobData = (data) => ({
    job_title:           (data.title || "").trim(),
    designation:         (data.designation || "").trim(),
    employment_type:     data.employmentType || "",
    location:            (data.location || "").trim(),
    vacancies:           data.vacancies ? parseInt(data.vacancies, 10) || 0 : 0,
    experience_required: data.experience || "",
    required_skills:     skills.join(", "),
    job_description:     (data.description || "").trim(),
    ...(data.publishedDate ? { published_date: data.publishedDate } : {}),
  });

  const validateSkills = () => {
    const valid = skills.length > 0;
    setSkillsError(!valid);
    return valid;
  };

  const onSubmitUpdate = handleSubmit((data) => {
    if (!validateSkills()) return;
    onUpdate(buildJobData(data));
  });

  const onSubmitPublish = handleSubmit((data) => {
    if (!validateSkills()) return;
    onPublish(buildJobData(data));
  });

  // A draft is meant to hold incomplete work-in-progress, so it only enforces
  // the one field a job needs to be identifiable in the Draft Jobs list —
  // everything else (skills included) can be filled in later. Using
  // trigger() instead of handleSubmit() here is what avoids running the
  // full-form "required" validation that every other field carries.
  const onSubmitDraft = async () => {
    const titleValid = await trigger("title");
    if (!titleValid) return;
    onSaveDraft(buildJobData(getValues()));
  };

  const chipStyle = {
    backgroundColor: `${primaryColor}0d`,
    borderColor:     `${primaryColor}40`,
    color:           primaryColor,
  };

  return (
    <form
      className="create-job-form"
      onSubmit={(e) => e.preventDefault()}
      autoComplete="off"
      noValidate
      style={{ "--hiring-primary": primaryColor }}
    >

      {/*
        Scrollable form body — same pattern as AddEmployeePopup (.add-employee-form-container).
        max-height + overflow-y: auto keeps content scrollable while the footer below
        stays in normal document flow and is always visible.
      */}
      <div className="create-job-form-body">

        {/* ── Section 1: Basic Information ── */}
        <div className="create-job-section">
          <p className="create-job-section-title">Basic Information</p>
          <div className="create-job-grid-2col">

            <StyledInputGroup>
              <StyledInputContainer>
                <StyledInput
                  type="text"
                  placeholder=" "
                  style={{ width: "100%", ...(errors.title ? { borderLeft: "10px solid red" } : {}) }}
                  {...register("title", {
                    required: true,
                    validate: (v) => validateTextField(v, "Job title") || true,
                  })}
                />
                <StyledLabel className="user-label">Job Title *</StyledLabel>
              </StyledInputContainer>
              {errors.title && <ErrorText>{errors.title.message || "Job title is required"}</ErrorText>}
            </StyledInputGroup>

            <StyledInputGroup>
              <StyledInputContainer>
                <StyledInput
                  type="text"
                  placeholder=" "
                  style={{ width: "100%", ...(errors.designation ? { borderLeft: "10px solid red" } : {}) }}
                  {...register("designation", {
                    required: true,
                    validate: (v) => validateTextField(v, "Designation") || true,
                  })}
                />
                <StyledLabel className="user-label">Designation *</StyledLabel>
              </StyledInputContainer>
              {errors.designation && <ErrorText>{errors.designation.message || "Designation is required"}</ErrorText>}
            </StyledInputGroup>

            <StyledSelectGroup>
              <StyledSelectContainer>
                <StyledSelect
                  width="100%"
                  style={errors.employmentType ? { borderLeft: "10px solid red" } : {}}
                  {...register("employmentType", { required: true })}
                >
                  <option value="">— Select Type —</option>
                  {EMPLOYMENT_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </StyledSelect>
                <StyledLabel className="user-label">Employment Type *</StyledLabel>
              </StyledSelectContainer>
              {errors.employmentType && <ErrorText>Employment type is required</ErrorText>}
            </StyledSelectGroup>

          </div>
        </div>

        {/* ── Section 2: Job Requirements ── */}
        <div className="create-job-section">
          <p className="create-job-section-title">Job Requirements</p>
          <div className="create-job-grid-2col">

            <StyledInputGroup>
              <StyledInputContainer>
                <StyledInput
                  type="text"
                  placeholder=" "
                  style={{ width: "100%", ...(errors.location ? { borderLeft: "10px solid red" } : {}) }}
                  {...register("location", {
                    required: true,
                    validate: (v) => validateTextField(v, "Location") || true,
                  })}
                />
                <StyledLabel className="user-label">Location *</StyledLabel>
              </StyledInputContainer>
              {errors.location && <ErrorText>{errors.location.message || "Location is required"}</ErrorText>}
            </StyledInputGroup>

            <StyledInputGroup>
              <StyledInputContainer>
                <StyledInput
                  type="number"
                  min="1"
                  step="1"
                  placeholder=" "
                  style={{ width: "100%", ...(errors.vacancies ? { borderLeft: "10px solid red" } : {}) }}
                  {...register("vacancies", {
                    required: true,
                    validate: (v) => validateNumericField(v, "Vacancies", { required: true, integer: true, min: 1 }) || true,
                  })}
                />
                <StyledLabel className="user-label">Vacancies *</StyledLabel>
              </StyledInputContainer>
              {errors.vacancies && <ErrorText>{errors.vacancies.message || "Enter a valid number of vacancies"}</ErrorText>}
            </StyledInputGroup>

            <div className="create-job-field-full">
              <StyledInputGroup>
                <StyledInputContainer>
                  <StyledInput
                    type="number"
                    min="0"
                    step="0.1"
                    placeholder=" "
                    style={{ width: "100%", ...(errors.experience ? { borderLeft: "10px solid red" } : {}) }}
                    {...register("experience", {
                      required: true,
                      min: { value: 0, message: "Experience cannot be negative" },
                      validate: (v) => !Number.isNaN(Number(v)) || "Experience must be a number",
                    })}
                  />
                  <StyledLabel className="user-label">Experience Required (years) *</StyledLabel>
                </StyledInputContainer>
                {errors.experience && (
                  <ErrorText>{errors.experience.message || "Experience is required"}</ErrorText>
                )}
              </StyledInputGroup>
            </div>

            {/* Skills chip input — full width */}
            <div className="create-job-field-full">
              <StyledInputGroup>
                <StyledInputContainer>
                  <StyledInput
                    type="text"
                    placeholder=" "
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyDown={handleSkillKeyDown}
                    style={{ width: "100%", ...(skillsError ? { borderLeft: "10px solid red" } : {}) }}
                  />
                  <StyledLabel className="user-label">Required Skills * — press Enter to add</StyledLabel>
                </StyledInputContainer>
                {skillsError && <ErrorText>At least one skill is required</ErrorText>}
              </StyledInputGroup>
              {skills.length > 0 && (
                <div className="create-job-skill-chips">
                  {skills.map((skill) => (
                    <span key={skill} className="create-job-skill-chip" style={chipStyle}>
                      {skill}
                      <button
                        type="button"
                        className="create-job-skill-remove"
                        onClick={() => removeSkill(skill)}
                        aria-label={`Remove ${skill}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>

        {/* ── Section 3: Job Description ── */}
        <div className="create-job-section">
          <p className="create-job-section-title">Job Description *</p>
          <StyledTextarea
            width="100%"
            height="140px"
            placeholder="Describe the role, responsibilities, and requirements..."
            style={errors.description ? { borderLeft: "10px solid red" } : {}}
            {...register("description", {
              required: true,
              validate: (v) => validateTextField(v, "Job description") || true,
            })}
          />
          {errors.description && <ErrorText>{errors.description.message || "Job description is required"}</ErrorText>}
        </div>

        {/* ── Section 4: Application Settings ── */}
        <div className="create-job-section create-job-section-last">
          <p className="create-job-section-title">Application Settings</p>
          <div className="create-job-grid-2col">
            <StyledInputGroup>
              <StyledInputContainer>
                <StyledInput
                  type="date"
                  min={getMinDate()}
                  max="9999-12-31"
                  style={{ width: "100%" }}
                  {...register("publishedDate")}
                />
                <StyledLabel hasValue className="user-label">Published Date</StyledLabel>
              </StyledInputContainer>
            </StyledInputGroup>
          </div>
        </div>

        <div className="create-job-actions">
          <SecondaryButton type="button" onClick={onCancel}>
            Cancel
          </SecondaryButton>
          {editJob ? (
            <PrimaryButton type="button" onClick={onSubmitUpdate}>Update Job</PrimaryButton>
          ) : (
            <>
              <SecondaryButton type="button" onClick={onSubmitDraft}>Save as Draft</SecondaryButton>
              <PrimaryButton type="button" onClick={onSubmitPublish}>Create Job</PrimaryButton>
            </>
          )}
        </div>

      </div>{/* end .create-job-form-body */}

    </form>
  );
};

export default CreateJobModal;
